import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiUsers, FiArrowRight, FiUserPlus, FiSettings, FiFolder,
  FiCheckCircle, FiMessageSquare
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const OrgTeams = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/organization/teams" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/organization" className="hover:text-blue-600">Organization</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Teams</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-4">
              <FiUsers className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Team Management</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Create and manage teams within departments for focused collaboration.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Teams in Organizations</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Teams are groups of employees within a department who work together on projects. 
                Each team has a Team Lead who manages day-to-day activities.
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Organization Hierarchy</h4>
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">Organization</span>
                  <span>→</span>
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">Department</span>
                  <span>→</span>
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 rounded">Team</span>
                  <span>→</span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded">Members</span>
                </div>
              </div>
            </div>
          </section>

          {/* Creating Teams */}
          <section className="mb-16" id="create-team">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Creating a Team</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Navigate to Department</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Go to your department's settings page</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Click "Create Team"</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Enter team name and description</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Assign Team Lead</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Select a team member to be the Team Lead</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Add Team Members</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Add employees to the team</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Team Lead Role */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Team Lead Capabilities</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiFolder className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Project Management</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Create and manage team projects</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiUserPlus className="w-6 h-6 text-green-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Task Assignment</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Assign tasks to team members</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiSettings className="w-6 h-6 text-purple-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Team Settings</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage team configuration</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiMessageSquare className="w-6 h-6 text-orange-600 mb-2" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Budget Requests</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Submit budget requests to department</p>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/organization/departments"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Department Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/organization/users"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">User Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default OrgTeams;
